/**
 * Lets the product-page offer picker handle more than three pieces.
 *
 * knott-bundles.js hard-codes `picks = [0,1,2]` and subtracts exactly ONE
 * free item in totals(), so without this the four-piece Black November row
 * throws on the fourth slot and under-reports the saving by half.
 *
 * THE FREE COUNT MUST MATCH THE LIVE SHOPIFY DISCOUNT. For the promotion that
 * is "Black November · Buy 2, Get 2 Free" — buy 2, get 2 at 100% off,
 * bracelets only, once per order:
 *
 *   free = qty >= 4 ? 2 : 0
 *
 * This holds whether or not the older offers are still switched on: while
 * "Buy 2, Get 1 Free · Storewide" is active it frees only ONE piece at four,
 * so Shopify picks Black November as the better deal either way.
 *
 * Verified against real storefront carts, never draft orders: draft orders
 * skip automatic discounts and will happily confirm a wrong number. Four
 * bracelets check out at $120 -> $60 with delivery FREE.
 *
 * A BxGy discount always lands on the LOWEST-priced eligible items, which is
 * why the ranking sorts ascending. Do not "fix" it to free the dearest piece;
 * checkout will not agree. It matters the moment a cart mixes prices — a $25
 * cord and a $30 V bracelet free is $55 off, not $60.
 *
 * ---------------------------------------------------------------------------
 * THE INITIALISATION RACE, and why connectedCallback is re-run below.
 *
 * knott-bundles.js is a module and this file is deferred, so the element is
 * DEFINED AND UPGRADED BEFORE this patch lands — customElements.define()
 * upgrades synchronously, while whenDefined().then() is a microtask after it.
 * Its connectedCallback runs:
 *
 *   picks = [0,1,2] ... qty = <checked radio> ... renderPicks() ... update()
 *   ... THEN addEventListener('change') and addEventListener('click')
 *
 * With the Black November default of 4, renderPicks() reaches picks[3],
 * throws on `st.p`, and the two addEventListener calls at the end never run —
 * so the picker renders nothing and responds to nothing. Loading this file
 * first does not help, for the microtask reason above.
 *
 * Two defences: pick() grows the array on demand so the crash cannot recur,
 * and any instance that already initialised is re-initialised once the
 * prototype is patched. Re-running connectedCallback is safe — the listeners
 * are instance properties, so adding them twice is a no-op.
 * ---------------------------------------------------------------------------
 */
(function () {
  var NAME = 'knott-tiers';
  if (!window.customElements || !customElements.whenDefined) return;

  customElements.whenDefined(NAME).then(function () {
    var K = customElements.get(NAME);
    if (!K || K.__knottMultiPatched) return;
    K.__knottMultiPatched = true;

    var origTotals = K.prototype.totals;
    var origUpdate = K.prototype.update;
    var origRender = K.prototype.renderPicks;
    var origPick = K.prototype.pick;

    function freeCount(qty) {
      return qty >= 4 ? 2 : 0;
    }

    K.prototype.growPicks = function (n) {
      if (!Array.isArray(this.picks) || this.picks.length >= n) return;
      var seed = this.picks[this.picks.length - 1] || this.picks[0];
      if (!seed) return;
      while (this.picks.length < n) {
        this.picks.push({ p: seed.p, v: seed.v, touched: false });
      }
    };

    // Any slot index is valid now, whatever order things initialise in.
    K.prototype.pick = function (i) {
      this.growPicks(Number(i) + 1);
      return origPick.call(this, i);
    };

    K.prototype.renderPicks = function () {
      this.growPicks(Number(this.qty) || 1);
      return origRender.call(this);
    };

    K.prototype.totals = function () {
      var n = Number(this.qty) || 1;

      // One piece has no discount, and the original owns the sold-out path.
      if (n <= 1) return origTotals.call(this);

      this.growPicks(n);

      var self = this;
      var chosen = this.picks.slice(0, n).map(function (_, i) {
        return self.pick(i).variant;
      });
      var was = chosen.reduce(function (sum, v) { return sum + v.price; }, 0);

      // Cheapest first. The tie-break keeps the LAST of equal prices, so a
      // row the shopper just changed is not the one that flips to FREE.
      var ranked = chosen
        .map(function (v, i) { return { price: v.price, i: i }; })
        .sort(function (a, b) { return a.price - b.price || b.i - a.i; });

      var free = ranked.slice(0, freeCount(n));
      var now = was - free.reduce(function (sum, f) { return sum + f.price; }, 0);
      var indexes = free.map(function (f) { return f.i; });

      return {
        chosen: chosen,
        was: was,
        now: now,
        freeIndex: indexes.length ? indexes[0] : -1,
        freeIndexes: indexes,
        ok: chosen.every(function (v) { return v.available; })
      };
    };

    K.prototype.update = function () {
      var n = Number(this.qty) || 1;
      if (n > 1) this.growPicks(n);

      origUpdate.call(this);
      if (n <= 1) return;

      // The original marks rows free using its own one-free rule, so every
      // row is restamped here rather than only the extra ones — otherwise a
      // row it freed by mistake keeps its FREE flag.
      var totals = this.totals();
      var self = this;
      var free = totals.freeIndexes || [];

      this.querySelectorAll('[data-kt-row]').forEach(function (row) {
        var i = Number(row.dataset.ktRow);
        var isFree = free.indexOf(i) > -1;
        row.classList.toggle('is-free', isFree);
        var price = row.querySelector('[data-kt-price]');
        if (!price || !totals.chosen[i]) return;
        price.innerHTML = isFree
          ? '<s>' + self.money(totals.chosen[i].price) + '</s><b class="kt-free">FREE</b>'
          : '<b>' + self.money(totals.chosen[i].price) + '</b>';
      });
    };

    // Repair anything that already initialised against the unpatched methods.
    document.querySelectorAll(NAME).forEach(function (el) {
      if (!el.isConnected || typeof el.connectedCallback !== 'function') return;
      try {
        el.connectedCallback();
      } catch (e) {
        /* A broken instance must not take the rest of the page with it. */
      }
    });
  });
})();